import type { VFC } from 'react';
import styled from 'styled-components';
import { FormattedMessage } from '@/components/formatted_message/FormattedMessage';
import LoginImage from '@/images/login-image.svg?react';
import { commonMessages } from '../messages';

const StyledHero = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 50%;
  height: 100%;
  padding: 120px 52px;

  h1 {
    color: #fff;
    font-size: 40px;
    line-height: 45px;
    text-align: center;
    font-weight: 700;
    margin-bottom: 45px;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.lg}px) {
    display: none;
  }

  @keyframes jump {
    0% {
      transform: translateY(0px);
    }

    5% {
      transform: translateY(-20px);
    }

    10% {
      transform: translateY(0px);
    }
  }

  @keyframes pulsate-fwd {
    0% {
      transform: scale(1);
    }
    50% {
      transform: scale(1.015);
    }
    100% {
      transform: scale(1);
    }
  }

  @keyframes shake-vertical {
    0%,
    100% {
      transform: translateY(0);
    }
    10%,
    30%,
    50%,
    70% {
      transform: translateY(-4px);
    }
    20%,
    40%,
    60% {
      transform: translateY(4px);
    }
    80% {
      transform: translateY(2.4);
    }
    90% {
      transform: translateY(-2.4);
    }
  }

  @keyframes gradient-animate {
    0% {
      background-size: 50% 85%, 50% 65%, 30% 55%, 75% 50%, auto;
    }

    50% {
      background-size: 20% 40%, 85% 55%, 45% 75%, 50% 40%, auto;
    }

    100% {
      background-size: 50% 85%, 50% 65%, 30% 55%, 75% 50%, auto;
    }
  }

  animation: gradient-animate 10s ease-in-out infinite;
  background-size: 50% 85%, 50% 75%, 30% 55%, 50% 40%, auto;
  background-position: 0% 0%, 100% 0%, 100% 100%, 0% 100%, 0% 0%;
  background-repeat: no-repeat, no-repeat, no-repeat, no-repeat, repeat;
  background-image: radial-gradient(
      ellipse farthest-corner at left top,
      rgba(0, 200, 250, 0.4) 0%,
      rgba(0, 200, 250, 0) 50%
    ),
    radial-gradient(ellipse farthest-corner at right top, rgba(0, 0, 0, 0.9) 0%, rgba(0, 0, 0, 0) 70%),
    radial-gradient(ellipse farthest-side at right bottom, rgba(220, 220, 220, 0.15) 0%, rgba(255, 255, 255, 0) 100%),
    radial-gradient(ellipse farthest-side at left bottom, rgba(0, 0, 0, 0.85) 0%, rgba(255, 255, 255, 0) 100%),
    linear-gradient(135deg, rgb(0 117 108) 0%, rgb(0 59 78) 50%, rgb(0 0 19) 100%);

  svg {
    width: 516px;
    height: 516px;

    .orange-dot,
    .blue-dot,
    .red-dot {
      animation: shake-vertical 3s linear infinite both;
    }

    .orange-dot {
      animation-delay: 0.125s;
    }

    .red-dot {
      animation-delay: 0.25s;
    }

    .buildings {
      .left-building {
        animation: jump 12s ease-in-out infinite both;
      }

      .right-building {
        animation: jump 12s ease-in-out infinite both;
        animation-delay: 0.2s;
      }
    }

    .cloud-storage {
      .cloud {
        animation: jump 12s ease-in-out infinite both;
        animation-delay: 3s;
      }

      .invoices {
        .sheet-1 {
          animation: jump 12s ease-in-out infinite both;
          animation-delay: 3.2s;
        }

        .sheet-2 {
          animation: jump 12s ease-in-out infinite both;
          animation-delay: 3.4s;
        }

        .sheet-3 {
          animation: jump 12s ease-in-out infinite both;
          animation-delay: 3.6s;
        }

        .sheet-4 {
          animation: jump 12s ease-in-out infinite both;
          animation-delay: 3.8s;
        }
      }
    }

    .bar-chart {
      .level-1 {
        animation: jump 12s ease-in-out infinite both;
        animation-delay: 6.4s;
      }

      .level-2 {
        animation: jump 12s ease-in-out infinite both;
        animation-delay: 6.2s;
      }

      .level-3 {
        animation: jump 12s ease-in-out infinite both;
        animation-delay: 6s;
      }
    }

    .pie-chart {
      .level-1 {
        animation: jump 12s ease-in-out infinite both;
        animation-delay: 9s;
      }

      .level-2 {
        animation: jump 12s ease-in-out infinite both;
        animation-delay: 9.1s;
      }

      .level-3 {
        animation: jump 12s ease-in-out infinite both;
        animation-delay: 9.2s;
      }
    }

    .csr,
    .kwh,
    .esg,
    .m3,
    .co2 {
      transform-origin: center;
      animation: pulsate-fwd 0.6s infinite both;
    }

    .csr {
      animation-delay: 0.05s;
    }

    .kwh {
      animation-delay: 0.1s;
    }

    .esg {
      animation-delay: 0.15s;
    }

    .m3 {
      animation-delay: 0.2s;
    }

    .co2 {
      animation-delay: 0.25s;
    }
  }
`;

export const Hero: VFC = () => (
  <StyledHero>
    <h1>
      <FormattedMessage {...commonMessages.catchphrase} values={{ br: <br /> }} />
    </h1>
    <LoginImage />
  </StyledHero>
);
